import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Upload, FileText, Search, Filter, Plus, ArrowLeft, Download, Trash2, ClipboardList, PenLine } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { api } from '../../lib/api';
import { statusLabel, statusTone, bytesToHuman, fmtDateTime } from '../../lib/docs';
import { toast } from 'sonner';

const YEAR = new Date().getFullYear() - 1;

export default function MemberDocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ tax_year: '', category_id: '', status: '', q: '' });
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.tax_year) params.tax_year = filters.tax_year;
      if (filters.status) params.status = filters.status;
      if (filters.category_id) params.category_id = filters.category_id;
      const [list, cats] = await Promise.all([
        api.myDocuments(params),
        api.documentCategories(),
      ]);
      setDocs(list);
      setCategories(cats);
    } catch { toast.error('Could not load documents'); }
    finally { setLoading(false); }
  }, [filters.tax_year, filters.status, filters.category_id]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    if (!q) return docs;
    return docs.filter((d) =>
      (d.original_name || '').toLowerCase().includes(q) ||
      (d.category_name || '').toLowerCase().includes(q) ||
      (d.description || '').toLowerCase().includes(q),
    );
  }, [docs, filters.q]);

  const years = useMemo(() => {
    const set = new Set(docs.map((d) => d.tax_year));
    set.add(YEAR);
    return Array.from(set).sort((a, b) => b - a);
  }, [docs]);

  return (
    <>
      <Header />
      <section className="py-10 bg-[#0c0a08] border-b border-neutral-800 text-white">
        <div className="max-w-[1240px] mx-auto px-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <Link to="/account" data-testid="back-to-account" className="text-[12px] uppercase tracking-wider text-[#f5b300] inline-flex items-center gap-1 hover:underline mb-2">
              <ArrowLeft className="w-3.5 h-3.5" /> Account
            </Link>
            <h1 className="text-[28px] md:text-[34px] font-extrabold text-[#f5b300]">Your tax document vault</h1>
            <p className="text-[13.5px] text-neutral-400 mt-1">Securely upload tax slips, receipts, and supporting files. Your tax specialist reviews them right here.</p>
          </div>
          <div className="flex gap-2.5">
            <Link to="/account/tax-file" data-testid="open-tax-file" className="inline-flex items-center gap-2 border border-neutral-700 hover:bg-white/10 text-white font-semibold px-4 py-3 rounded-md text-[13px]">
              <ClipboardList className="w-4 h-4" /> Tax file
            </Link>
            <Link to="/account/documents/upload" data-testid="open-upload" className="inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-5 py-3 rounded-md text-[13px]">
              <Upload className="w-4 h-4" /> Upload documents
            </Link>
          </div>
        </div>
      </section>

      <section className="py-10 bg-white">
        <div className="max-w-[1240px] mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">
            <div className="md:col-span-2 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                data-testid="docs-search"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="Search documents..."
                className="w-full pl-9 pr-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none"
              />
            </div>
            <select data-testid="filter-year" value={filters.tax_year} onChange={(e) => setFilters({ ...filters, tax_year: e.target.value })}
              className="px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none">
              <option value="">All tax years</option>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <select data-testid="filter-category" value={filters.category_id} onChange={(e) => setFilters({ ...filters, category_id: e.target.value })}
              className="px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none">
              <option value="">All categories</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none">
              <option value="">All statuses</option>
              {Object.entries({ uploaded: 'Uploaded', in_review: 'In review', reviewed: 'Reviewed', accepted: 'Accepted',
                rejected: 'Rejected', needs_clarification: 'Needs clarification', signed: 'Signed', filed: 'Filed' }).map(
                ([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="py-20 text-center text-[13.5px] text-neutral-500">Loading documents…</div>
          ) : filtered.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="overflow-hidden border border-neutral-100 rounded-xl">
              <table className="w-full text-[13.5px]">
                <thead className="bg-[#fbf7ee] text-[12px] uppercase tracking-wider text-neutral-700">
                  <tr>
                    <th className="text-left px-4 py-3">Document</th>
                    <th className="text-left px-4 py-3">Category</th>
                    <th className="text-left px-4 py-3">Year</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Uploaded</th>
                    <th className="text-right px-4 py-3">Size</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((d) => (
                    <tr key={d.id} data-testid={`doc-row-${d.id}`}
                        onClick={() => nav(`/account/documents/${d.id}`)}
                        className="border-t border-neutral-100 hover:bg-[#fbf7ee]/50 cursor-pointer transition">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <FileText className="w-4 h-4 text-[#c89200] shrink-0" />
                          <div className="min-w-0">
                            <p className="font-semibold text-[#14110f] truncate">{d.original_name}</p>
                            {d.description && <p className="text-[12px] text-neutral-500 truncate">{d.description}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-neutral-700">{d.category_name}</td>
                      <td className="px-4 py-3 text-neutral-700">{d.tax_year}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${statusTone(d.status)}`}>
                          {statusLabel(d.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-neutral-500 text-[12.5px]">{fmtDateTime(d.uploaded_at)}</td>
                      <td className="px-4 py-3 text-right text-neutral-500 text-[12.5px]">{bytesToHuman(d.size)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
}

function EmptyState() {
  return (
    <div className="py-20 text-center border-2 border-dashed border-neutral-200 rounded-xl bg-[#fbf7ee]/40">
      <FileText className="w-12 h-12 mx-auto text-neutral-300 mb-3" />
      <p className="text-[15px] font-semibold text-[#14110f]">No documents yet</p>
      <p className="text-[13px] text-neutral-500 mt-1 max-w-md mx-auto">Upload tax slips, receipts and supporting documents — your tax specialist sees everything you add here.</p>
      <Link to="/account/documents/upload" className="mt-5 inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-5 py-2.5 rounded-md text-[13px]">
        <Plus className="w-4 h-4" /> Upload your first document
      </Link>
    </div>
  );
}
